import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { FormTextField, FormSelectField, FormPasswordField, FormCheckboxField } from '../molecules/form-elements';
import { SelectOption } from '../molecules/form-elements/FormSelectField';
import StyledDatePicker from '../atoms/StyledDatePicker';
import dayjs from 'dayjs';

export type FieldDefinition = {
  name: string;
  type: 'text' | 'password' | 'select' | 'checkbox' | 'date';
  label: string;
  placeholder?: string;
  options?: SelectOption[];
  defaultValue?: string | boolean;
  disabled?: boolean;
};

type DynamicFormFieldsProps<TFieldValues extends FieldValues> = {
  fields: FieldDefinition[];
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  isSubmitting: boolean;
  getValues: UseFormGetValues<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
};

const styles = {
  select: {
    color: 'neutral.25',
    '& .MuiSelect-icon': { color: 'neutral.300' },
  },
  menuPaper: {
    backgroundColor: 'background.paper',
    color: 'neutral.25',
    maxHeight: 300,
  },
  checkboxLabel: {
    color: 'neutral.200',
  },
  checkbox: {
    pl: 0,
  },
};

export const DynamicFormFields = <TFieldValues extends FieldValues>({
  fields,
  control,
  errors,
  isSubmitting,
}: DynamicFormFieldsProps<TFieldValues>) => {
  return (
    <>
      {fields.map((field) => {
        const fieldName = field.name as Path<TFieldValues>;
        const error = errors[fieldName];

        switch (field.type) {
          case 'text':
            return (
              <FormTextField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                variant='outlined'
                fullWidth
                type='text'
                error={!!error}
                helperText={error?.message as string}
                disabled={isSubmitting || field.disabled}
                placeholder={field.placeholder}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
                defaultValue={field.defaultValue}
              />
            );
          case 'password':
            return (
              <FormPasswordField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                error={!!error}
                helperText={error?.message as string}
                disabled={isSubmitting || field.disabled}
                slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
              />
            );
          case 'select':
            return (
              <FormSelectField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                error={!!error}
                helperText={error?.message as string}
                options={field.options ?? []}
                FormControlProps={{
                  fullWidth: true,
                  disabled: isSubmitting || field.disabled,
                }}
                SelectProps={{
                  variant: 'outlined',
                  sx: styles.select,
                  MenuProps: {
                    slotProps: {
                      paper: {
                        sx: styles.menuPaper,
                      },
                    },
                  },
                }}
              />
            );
          case 'checkbox':
            return (
              <FormCheckboxField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                error={!!error}
                helperText={error?.message as string}
                disabled={isSubmitting || field.disabled}
                FormControlLabelProps={{ sx: styles.checkboxLabel }}
                CheckboxProps={{ sx: styles.checkbox }}
              />
            );
          case 'date':
            return (
              <Controller
                key={fieldName}
                name={fieldName}
                control={control}
                render={({ field: controlledField }) => (
                  <StyledDatePicker
                    value={controlledField.value ? dayjs(controlledField.value as string) : null}
                    name={controlledField.name}
                    inputRef={controlledField.ref}
                    label={field.label}
                    error={!!error}
                    helperText={error?.message as string}
                    onChange={(newValue) => controlledField.onChange(newValue?.format('YYYY-MM-DD') ?? '')}
                    disabled={isSubmitting || field.disabled}
                    slotProps={{ field: { onBlur: controlledField.onBlur } }}
                  />
                )}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};
